import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { Mic2, Trash2, Search, Loader2 } from "lucide-react";

// —— Types ——
type Podcast = {
  id: string;
  title: string;
  description: string;
  video_url: string;
  created_at: string;
};

// —— Utils ——
// Normalise (watch?v=...), youtu.be/xxxxx, shorts/xxxxx → embed/xxxxx
const toYouTubeEmbed = (raw: string): string => {
  try {
    const url = new URL(raw);
    // youtu.be/<id>
    if (url.hostname.includes("youtu.be")) {
      const id = url.pathname.replace("/", "");
      return `https://www.youtube.com/embed/${id}`;
    }
    // youtube.com/shorts/<id>
    if (url.pathname.startsWith("/shorts/")) {
      const id = url.pathname.split("/")[2];
      return `https://www.youtube.com/embed/${id}`;
    }
    // youtube.com/watch?v=<id>
    const v = url.searchParams.get("v");
    if (v) return `https://www.youtube.com/embed/${v}`;
    // Déjà un embed ?
    if (url.pathname.includes("/embed/")) return raw;
    // Fallback (on tente de remplacer watch?v=)
    return raw.replace("watch?v=", "embed/");
  } catch {
    // Fallback si ce n'est pas une URL valide
    return raw.includes("watch?v=") ? raw.replace("watch?v=", "embed/") : raw;
  }
};

// Date FR lisible
const formatFrDate = (iso: string) =>
  new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

export const PodcastPage = () => {
  const [podcasts, setPodcasts] = useState<Podcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [confirmId, setConfirmId] = useState<string | null>(null); // modale de confirmation
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // 🔐 Admin (tu peux le déplacer vers un .env si tu préfères)
  const adminEmail = "cyprien.conseillon@icloud.com";

  // —— Data fetchers ——
  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("podcasts")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Erreur récupération podcasts :", error);
      } else {
        setPodcasts(data || []);
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUserEmail(user?.email ?? null);
    })();
  }, []);

  // —— Derived data —— (filtre recherche)
  const filtered = useMemo(() => {
    if (!query.trim()) return podcasts;
    const q = query.toLowerCase();
    return podcasts.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q)
    );
  }, [podcasts, query]);

  // —— Actions ——
  const requestDelete = (id: string) => {
    if (userEmail !== adminEmail) {
      // petite UX douce côté UI, pas d'alert/confirm natifs
      setConfirmId(null);
      return;
    }
    setConfirmId(id);
  };

  const handleDelete = async () => {
    if (!confirmId) return;
    if (userEmail !== adminEmail) {
      setConfirmId(null);
      return;
    }
    setDeletingId(confirmId);
    const { error } = await supabase.from("podcasts").delete().eq("id", confirmId);
    if (error) {
      console.error("Erreur lors de la suppression :", error.message);
    } else {
      setPodcasts((prev) => prev.filter((p) => p.id !== confirmId));
    }
    setDeletingId(null);
    setConfirmId(null);
  };

  return (
    <section className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8">
        <div className="flex flex-col items-start sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="inline-flex size-10 items-center justify-center rounded-2xl bg-orange-100 dark:bg-orange-900/30">
              <Mic2 className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
                Podcast
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Les épisodes vidéo publiés par ordre chronologique.
              </p>
            </div>
          </div>

          {/* Barre de recherche */}
          <div className="w-full sm:w-80">
            <label className="sr-only" htmlFor="podcast-search">Recherche</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                id="podcast-search"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher un épisode…"
                className="w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 pl-10 pr-3 py-2 text-sm
                           text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2
                           focus:ring-orange-400/70"
              />
            </div>
          </div>
        </div>

        {/* Contenu */}
        <div className="mt-6 pb-12">
          {loading ? (
            // —— Skeletons —— (3 cartes)
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm overflow-hidden"
                >
                  <div className="relative w-full pt-[56.25%] bg-gray-100 dark:bg-gray-800 animate-pulse" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
                    <div className="h-3 w-full bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
                    <div className="h-3 w-5/6 bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center border border-dashed border-gray-300 dark:border-gray-800 rounded-2xl py-16">
              <Mic2 className="w-8 h-8 text-gray-400 mb-2" />
              <p className="text-gray-700 dark:text-gray-300 font-medium">
                Aucun épisode trouvé
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Essaie d’ajuster ta recherche.
              </p>
            </div>
          ) : (
            // —— Grille responsive —— 
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((podcast) => {
                const embedUrl = toYouTubeEmbed(podcast.video_url);
                const isDeleting = deletingId === podcast.id;
                return (
                  <article
                    key={podcast.id}
                    className="group rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm overflow-hidden
                               hover:shadow-md transition-shadow"
                  >
                    {/* Media responsive 16:9 */}
                    <div className="relative w-full pt-[56.25%] bg-black">
                      <iframe
                        className="absolute inset-0 w-full h-full"
                        src={embedUrl}
                        title={podcast.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        loading="lazy"
                      />
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 line-clamp-2">
                        {podcast.title}
                      </h2>
                      <p className="mt-1 text-sm text-gray-700 dark:text-gray-300 line-clamp-3">
                        {podcast.description}
                      </p>

                      <div className="mt-3 flex items-center justify-between">
                        <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium
                                          bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300 border border-orange-100 dark:border-orange-900/40">
                          Publié le {formatFrDate(podcast.created_at)}
                        </span>

                        {userEmail === adminEmail && (
                          <button
                            onClick={() => requestDelete(podcast.id)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 dark:border-red-900/50
                                       bg-red-50 dark:bg-red-900/20 px-2.5 py-1.5 text-xs font-medium text-red-700 dark:text-red-300
                                       hover:bg-red-100 dark:hover:bg-red-900/30 focus-visible:outline-none focus-visible:ring-2
                                       focus-visible:ring-red-400/70 transition-colors"
                            aria-label="Supprimer l'épisode"
                          >
                            {isDeleting ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                            <span>Supprimer</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* —— Modale de confirmation —— */}
      {confirmId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setConfirmId(null)} />
          <div className="relative w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Supprimer l’épisode ?</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Cette action est irréversible. L’épisode sera définitivement supprimé.
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmId(null)}
                className="rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm
                           text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400/60"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 text-sm
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
              >
                <Trash2 className="w-4 h-4" />
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
